// The suppression list: who must not be contacted, with the reason (SPEC §5, PD20). It is applied before every
// audience and again before every send. The partner portal is never told why someone is on it.
import { z } from 'zod'
import { ChannelOrAll, Id, IsoDateTime, LeadCode, MaskedMobile } from './common'

export const SuppressionReason = z.enum([
  'dnd',
  'wa_stop',
  'wa_block',
  'wa_spam',
  'ivr_stop',
  'ivr_dnc',
  'complaint',
  'consent_withdrawn',
])
export type SuppressionReason = z.infer<typeof SuppressionReason>

export const SuppressionEntry = z.object({
  id: Id,
  lead_code: LeadCode,
  mobile_masked: MaskedMobile,
  reason: SuppressionReason,
  channel: ChannelOrAll,
  note: z.string(),
  at: IsoDateTime,
})
export type SuppressionEntry = z.infer<typeof SuppressionEntry>

export const AddSuppressionInput = z.object({
  lead_code: LeadCode,
  reason: SuppressionReason,
  channel: ChannelOrAll,
  note: z.string().trim().max(200).optional(),
})
export type AddSuppressionInput = z.infer<typeof AddSuppressionInput>

/** D1 (owner 2026-11-03, D-Q1): at most 10 lakh numbers and codes in one import. */
export const DND_IMPORT_MAX = 1_000_000
/** D1: one RabbitMQ part to the lead registry holds at most this many mobiles (or lead codes). */
export const DND_PART_SIZE = 1000

/**
 * D1: the list as typed or as the mobile column of a CSV: mobiles written any way (+91 92213 01979, 07440666633,
 * 7387 7513 99) and lead codes (FF-…), separated by commas, new lines, semicolons or tabs. Mobiles travel only to the
 * lead registry (on RabbitMQ); the campaign portal never stores or logs one.
 */
export const ImportDndInput = z.object({
  list: z.string().max(24_000_000, 'At most 10 lakh numbers in one import: split the file.'),
  file_name: z.string().trim().max(200).optional(),
})
export type ImportDndInput = z.infer<typeof ImportDndInput>

const LEAD_CODE = /^FF-[0-9A-HJKMNP-TV-Z]{9}$/

/** One typed number → its 10 digits (6–9 first), or null. Takes off +91, 91 (12 digits), 0 (11 digits). */
export function cleanMobile(typed: string): string | null {
  let digits = typed.replace(/[\s\-().]/g, '')
  if (digits.startsWith('+')) digits = digits.slice(1)
  if (!/^\d+$/.test(digits)) return null
  if (digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2)
  else if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1)
  return /^[6-9]\d{9}$/.test(digits) ? digits : null
}

export type DndList = { mobiles: string[]; codes: string[]; invalid: number; repeated: number }

/** D1: the list split into clean mobiles and lead codes (each once), and how many items were invalid or repeated. */
export function splitDndList(list: string): DndList {
  const mobiles = new Set<string>()
  const codes = new Set<string>()
  let invalid = 0
  let repeated = 0
  for (const raw of list.split(/[\r\n,;\t]+/)) {
    const item = raw.trim().replace(/^"|"$/g, '').trim()
    if (!item) continue
    const code = item.toUpperCase()
    const into = LEAD_CODE.test(code) ? codes : mobiles
    const value = into === codes ? code : cleanMobile(item)
    if (!value) invalid += 1
    else if (into.has(value)) repeated += 1
    else into.add(value)
  }
  return { mobiles: [...mobiles], codes: [...codes], invalid, repeated }
}

/**
 * D1: the mobile column of a CSV file (named mobile, phone, number, msisdn or contact; else the first column), one per
 * line. A header row is skipped. Only this column leaves the browser: names and other columns never do.
 */
export function mobilesFromCsv(text: string): string {
  const rows = text.split(/\r?\n/).filter((row) => row.trim())
  if (!rows.length) return ''
  const cells = (row: string) => row.split(/[,;\t]/).map((cell) => cell.trim().replace(/^"|"$/g, '').trim())
  const header = cells(rows[0]!).map((cell) => cell.toLowerCase())
  const named = header.findIndex((cell) => /^(mobile|phone|number|msisdn|contact)/.test(cell))
  const hasHeader = named >= 0 || !header.some((cell) => cleanMobile(cell) || LEAD_CODE.test(cell.toUpperCase()))
  const column = Math.max(named, 0)
  return (hasHeader ? rows.slice(1) : rows).map((row) => cells(row)[column] ?? '').join('\n')
}

/** D1: the import was taken; its parts are on their way to the lead registry (progress: listDndImports). */
export const ImportDndAnswer = z.object({
  import_id: Id,
  mobiles: z.number().int().min(0).describe('clean mobiles, each once'),
  codes: z.number().int().min(0).describe('lead codes, each once'),
  invalid: z.number().int().min(0).describe('items that were neither a mobile nor a lead code: not sent, not echoed'),
  repeated: z.number().int().min(0),
  parts: z.number().int().min(1).describe('RabbitMQ parts of up to 1,000'),
})
export type ImportDndAnswer = z.infer<typeof ImportDndAnswer>

export const DndImportStatus = z.enum(['importing', 'done'])
export type DndImportStatus = z.infer<typeof DndImportStatus>

/** D1: one DND import and how far it is (the lead registry answers part by part). Counts only, never a mobile. */
export const DndImport = z.object({
  id: Id,
  at: IsoDateTime,
  by_name: z.string(),
  file_name: z.string().nullable(),
  mobiles: z.number().int().min(0),
  codes: z.number().int().min(0),
  invalid: z.number().int().min(0),
  repeated: z.number().int().min(0),
  parts: z.number().int().min(1),
  parts_done: z.number().int().min(0),
  known: z.number().int().min(0).describe('mobiles the lead registry already knew'),
  new_people: z.number().int().min(0).describe('mobiles it did not know: made there, mobile only, marked DND'),
  not_found: z.number().int().min(0).describe('lead codes nobody has'),
  added: z.number().int().min(0).describe('new rows on the suppression list'),
  already_listed: z.number().int().min(0),
  status: DndImportStatus,
  done_at: IsoDateTime.nullable(),
})
export type DndImport = z.infer<typeof DndImport>

/**
 * A1 (2026-11-03): is the DND data fresh enough to contact people? A consent or marketing campaign cannot be approved,
 * started or resumed while it was never loaded or is older than `max_age_days` (lawyer: DND numbers are never contacted).
 */
export const DndStatus = z.object({
  loaded_at: IsoDateTime.nullable().describe("the last DND import that finished (later the registry's scrub too); null = never"),
  max_age_days: z.number().int().min(1),
  fresh: z.boolean().describe('loaded and not older than max_age_days'),
  listed: z.number().int().min(0).describe('open suppression rows with the reason dnd'),
})
export type DndStatus = z.infer<typeof DndStatus>

export const RemoveSuppressionInput = z.object({
  reason: z.string().trim().min(3, 'Say why (it goes in the audit log)').max(200),
})
export type RemoveSuppressionInput = z.infer<typeof RemoveSuppressionInput>

export interface SuppressionApi {
  /** newest first */
  listSuppression(): Promise<SuppressionEntry[]>
  addSuppression(input: AddSuppressionInput): Promise<SuppressionEntry>
  importDnd(input: ImportDndInput): Promise<ImportDndAnswer>
  /** D1: the last 20 DND imports, newest first */
  listDndImports(): Promise<DndImport[]>
  getDndStatus(): Promise<DndStatus>
  removeSuppression(id: string, input: RemoveSuppressionInput): Promise<void>
}
